"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, ShoppingCart, Building2, Globe, Compass, Rocket, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
const audienceSegments = [{
  id: 1,
  icon: MapPin,
  badge: "Local Reach",
  title: "Local Service Businesses",
  description: "Clinics, legal firms, contractors, and hospitality businesses targeting high-intent local clients across Dubai, Abu Dhabi, and specific emirates."
}, {
  id: 2,
  icon: ShoppingCart,
  badge: "E-commerce",
  title: "E-commerce Stores",
  description: "Brands competing for UAE product keywords, category visibility, and high-converting commercial searches to scale transaction revenue sustainably."
}, {
  id: 3,
  icon: Building2,
  badge: "B2B & Pipeline",
  title: "B2B Companies",
  description: "Specialised B2B enterprises needing to generate qualified decision-maker leads, build topical authority, and capture long-cycle purchase searches."
}, {
  id: 4,
  icon: Globe,
  badge: "Market Entry",
  title: "International Businesses",
  description: "Global organisations and regional brands entering the Dubai and GCC market for the first time seeking reliable search presence from day one."
}, {
  id: 5,
  icon: Compass,
  badge: "Global Expansion",
  title: "Dubai Businesses Targeting Global Customers",
  description: "Established regional operators expanding outward to attract UK, European, North American, or international search traffic."
}, {
  id: 6,
  icon: Rocket,
  badge: "Early Growth",
  title: "Startups",
  description: "Early-stage companies needing to construct technical search foundations, brand authority, and organic visibility from scratch with focused budgets."
}];
export default function TargetAudience() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="targetaudience" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4 rounded-full px-3.5 py-1 text-xs font-medium tracking-wide uppercase">
            Client Focus
          </Badge>

          <h2 className="font-serif text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl text-foreground">
            Who My SEO Services Are For
          </h2>

          <p className="mt-4 text-lg md:text-xl text-muted-foreground font-default">
            I serve businesses that take organic search seriously and want sustainable growth.
          </p>
        </motion.div>

        {/* 2x3 Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {audienceSegments.map((segment, i) => {
          const IconComponent = segment.icon;
          return <motion.div key={segment.id} variants={itemVariants} data-index={i} className="h-full">
                <Card className="group relative flex h-full flex-col justify-between rounded-xl border border-border bg-card p-6 md:p-8 shadow-xs hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-300">
                  <CardHeader className="p-0 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
                        <IconComponent className="size-6" aria-hidden="true" />
                      </div>
                      <Badge variant="outline" className="rounded-full border-border bg-background px-3 py-0.5 text-xs text-muted-foreground">
                        {segment.badge}
                      </Badge>
                    </div>

                    <CardTitle className="font-serif text-xl font-semibold text-card-foreground">
                      {segment.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-0 mt-3">
                    <p className="text-sm md:text-base leading-relaxed text-muted-foreground font-default">
                      {segment.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom CTA & Statement */}
        <motion.div initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-16 text-center">
          <p className="mx-auto max-w-2xl text-base md:text-lg font-medium text-foreground">
            All share one thing: they&apos;re ready for sustainable organic growth, not quick fixes.
          </p>

          <div className="mt-6 flex justify-center">
            <Button asChild size="lg" className="rounded-md bg-primary px-7 py-3 text-base font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <span>{"Let's Discuss Your SEO Strategy"}</span>
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}